import { Column } from "primereact/column";
import { GenericTableColumns, GenericTableFilterOption } from "../models/GenericTableViewModel";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../enum/TableView";
import { Avatar } from "primereact/avatar";
import { FormControl, Stack } from "@mui/material";
import { Button } from "primereact/button";
import dayjs from "dayjs";
import { Dropdown } from "primereact/dropdown";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { MutableRefObject } from "react";
import { Calendar } from "primereact/calendar";

interface inputPrams {
  columns: GenericTableColumns[],
  toast: MutableRefObject<any>
}

export default function GenericTableDynamicBody({ columns, toast }: inputPrams) {

  const selectItemTemplate = (option: GenericTableFilterOption) => {
    return <span>{option.label}</span>;
  };

  const statusBodyTemplate = (value: string) => {
    switch (value) {
      case "success":
        return (
          <div>
            <i
              id="statusIconSuccess"
              style={{ color: "#2dd36f", width: "100%", textAlign: "center" }}
              className="pi pi-check-circle"
            ></i>
            <Tooltip target="#statusIconSuccess" content="Success" position="top" />
          </div>
        );
      case "completed":
        return (
          <div>
            <i
              id="statusIconSuccess"
              style={{ color: "#2dd36f", width: "100%", textAlign: "center" }}
              className="pi pi-check-circle"
            ></i>
            <Tooltip target="#statusIconSuccess" content="Completed" position="top" />
          </div>
        );
      case "warning":
        return (
          <div>
            <i
              id="statusIconWarning"
              style={{ color: "#ffc409", width: "100%", textAlign: "center" }}
              className="pi pi-info-circle"
            ></i>
            <Tooltip target="#statusIconWarning" content="Expiring soon" position="top" />
          </div>
        );
      case "inprogress":
        return (
          <div>
            <i
              id="statusIconWarning"
              style={{ color: "#ffc409", width: "100%", textAlign: "center" }}
              className="pi pi-info-circle"
            ></i>
            <Tooltip target="#statusIconWarning" content="In Progress" position="top" />
          </div>
        );
      case "none":
        return (
          <div>
            <i
              id="statusIconNone"
              style={{ color: "#D3D3D3", width: "100%", textAlign: "center" }}
              className="pi pi-minus"
            ></i>
            <Tooltip target="#statusIconNone" content="No File Uploaded" position="top" />
          </div>
        );
      case "abandoned":
        return (
          <div>
            <i
              id="statusIconFail"
              style={{ color: "#eb445a", width: "100%", textAlign: "center" }}
              className="pi pi-times-circle"
            ></i>
            <Tooltip target="#statusIconFail" content="Abandoned" position="top" />
          </div>
        );
      case "fail":
      default:
        return (
          <div>
            <i
              id="statusIconFail"
              style={{ color: "#eb445a", width: "100%", textAlign: "center" }}
              className="pi pi-times-circle"
            ></i>
            <Tooltip target="#statusIconFail" content="Expired" position="top" />
          </div>
        );
    }
  };

  const importanceBodyTemplate = (value: string) => {
    switch (value) {
      case "urgent":
        return (
          <div>
            <i
              id="importanceIconUrgent"
              style={{ color: "#eb445a", width: "100%", textAlign: "center" }}
              className="pi pi-exclamation-triangle"
            ></i>
            <Tooltip target="#importanceIconUrgent" content="Urgent" position="top" />
          </div>
        );
      case "important":
        return (
          <div>
            <i
              id="importanceIconImportant"
              style={{ color: "#ffc409", width: "100%", textAlign: "center" }}
              className="pi pi-minus"
            ></i>
            <Tooltip target="#importanceIconImportant" content="Important" position="top" />
          </div>
        );
      case "notice":
      default:
        return (
          <div>
            <i
              id="importanceIconNotice"
              style={{ color: "#2dd36f", width: "100%", textAlign: "center" }}
              className="pi pi-bell"
            ></i>
            <Tooltip target="#importanceIconNotice" content="Notice" position="top" />
          </div>
        );
    }
  };

  const setFilterBody = (options: any, col: GenericTableColumns) => {
    if (!col.filter || !col.filterOption) {
      return null;
    }
    switch (col.filterOption.type) {
      case GenericTableFilterTypes.Text:
        return (
          <InputText value={options.value} onChange={e => options.filterCallback(e.target.value)} />
        );
      case GenericTableFilterTypes.Boolean:
        return (
          <TriStateCheckbox value={options.value} onChange={e => options.filterCallback(e.value)} />
        );
      case GenericTableFilterTypes.Status:
        return (
          <TriStateCheckbox value={options.value} onChange={e => options.filterCallback(e.value)} />
        );
      case GenericTableFilterTypes.Date:
        return (
          <Calendar inline={true} value={options.value} onChange={e => options.filterCallback(e.value)} />
        );
      case GenericTableFilterTypes.Select:
        return (
          <Dropdown
            value={options.value}
            options={col.filterOption.options}
            onChange={e => options.filterCallback(e.value, options.index)}
            itemTemplate={selectItemTemplate}
            placeholder={col.filterOption.placeholder}
            className="p-column-filter"
            showClear
          />
        );
      default:
        return null;
    }
  };

  const formatText = (text: string) => {
    return text
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const setColumnBody = (rowData: any, col: GenericTableColumns) => {
    switch (col.type) {
      case GenericTableColumTypes.Text:
        return null;
      case GenericTableColumTypes.Text_:
        return <label>{rowData[col.field] ? formatText(rowData[col.field] as string) : ""}</label>;
      case GenericTableColumTypes.Boolean:
        return (
          <i
            style={{
              color: rowData[col.field] ? "#2dd36f" : "#eb445a",
              width: "100%",
              textAlign: "center",
            }}
            className={classNames("pi", {
              "true-icon pi-check-circle": rowData[col.field] || rowData[col.field] === "Active",
              "false-icon pi-times-circle": !(
                rowData[col.field] || rowData[col.field] === "Active"
              ),
            })}
          ></i>
        );
      case GenericTableColumTypes.Status:
        return statusBodyTemplate(rowData[col.field]);
      case GenericTableColumTypes.Importance:
        return importanceBodyTemplate(rowData[col.field]);
      case GenericTableColumTypes.DateTime:
        return (
          <label>
            {rowData[col.field] ? dayjs(rowData[col.field]).format("YYYY/MM/DD HH:mm") : ""}
          </label>
        );
      case GenericTableColumTypes.Date:
        return (
          <label>{rowData[col.field] ? dayjs(rowData[col.field]).format("YYYY/MM/DD") : ""}</label>
        );
      case GenericTableColumTypes.Image:
        return (
          <Stack direction="row" spacing={2}>
            <FormControl style={{ margin: "auto" }}>
              <Avatar image={rowData[col.field]} size="normal" shape="circle" />
            </FormControl>
          </Stack>
        );
      case GenericTableColumTypes.Link:
        return (
          <div style={{ width: "100%" }}>
            <a href={rowData[col.field]} target="_blank">
              Open
            </a>
            <ContentCopyOutlinedIcon
              onClick={e => {
                e?.preventDefault();
                navigator.clipboard.writeText(rowData[col.field]);
                toast.current.show({ severity: "success", summary: "link copied ", life: 1000 });
              }}
              style={{ float: "right" }}
            />
          </div>
        );
      case GenericTableColumTypes.Contact:
        return (
          <Stack direction="row" spacing={2}>
            {/* <FormControl style={{ margin: "auto" }}>
              <Avatar
                label={rowData[col.contactDetails?.labelField || ""]?.charAt(0)}
                image={rowData[col.contactDetails?.avatarSrc || ""]}
                size="normal"
                shape="circle"
              />
            </FormControl> */}
            <FormControl style={{ width: "45%" }} sx={{ flexGrow: 1 }}>
              <p style={{ margin: 0, fontSize: "small" }}>
                {rowData[col.contactDetails?.cellField || ""]}
              </p>
              <p style={{ margin: 0, fontSize: "small" }}>
                {rowData[col.contactDetails?.emailField || ""]}
              </p>
            </FormControl>
          </Stack>
        );
      case GenericTableColumTypes.Actions:
        return <Button size="small" icon="pi pi-ellipsis-h" />;
      case GenericTableColumTypes.SETTINGS:
        return <i className="pi pi-spin pi-cog" style={{ fontSize: '2rem' }}></i>;
      case GenericTableColumTypes.HTML:
        return <div dangerouslySetInnerHTML={{ __html: rowData[col.field] }} />
      case GenericTableColumTypes.Template:
        if (!col.template) throw new Error("Template column must have a template function");
        return col.template(rowData);
      default:
        return null;
    }
  };

  return columns
    .filter(e => e.visible)
    .map((col, i) => {
      if (col.type === GenericTableColumTypes.Text) {
        return (
          <Column
            style={{ maxWidth: "20vw", textWrap: "nowrap", textOverflow: "ellipsis" }}
            key={`${col.field}-${i}`}
            filter={col.filter}
            sortable={col.sort}
            field={col.field}
            header={col.header}
            showFilterMatchModes={false}
            filterElement={e => setFilterBody(e, col)}
          />
        );
      }
      return (
        <Column
          style={{ maxWidth: "20vw", textWrap: "nowrap", textOverflow: "ellipsis" }}
          key={`${col.field}-${i}`}
          filter={col.filter}
          sortable={col.sort}
          field={col.field}
          header={col.header}
          showFilterMatchModes={false}
          body={e => setColumnBody(e, col)}
          filterElement={e => setFilterBody(e, col)}
        />
      );
    });
}